import { useQuery, useQueryClient } from '@tanstack/react-query';
import { inventoryKeys } from './keys.js';
import { useInventoryClient } from './provider.js';
import { useIdempotentMutation } from './useIdempotentMutation.js';
export function useItems(filter = {}, options) {
    const client = useInventoryClient();
    return useQuery({
        queryKey: inventoryKeys.items.list(filter),
        queryFn: ({ signal }) => client.items.list(filter, signal),
        ...options,
    });
}
export function useItem(itemId, options) {
    const client = useInventoryClient();
    return useQuery({
        queryKey: inventoryKeys.items.detail(itemId ?? ''),
        queryFn: ({ signal }) => client.items.get(itemId, signal),
        enabled: Boolean(itemId),
        ...options,
    });
}
/** The scan-to-item path. A 404 here is an ordinary outcome, not an exception. */
export function useItemByCode(code, options) {
    const client = useInventoryClient();
    return useQuery({
        queryKey: inventoryKeys.items.byCode(code ?? ''),
        queryFn: ({ signal }) => client.items.getByCode(code, signal),
        enabled: Boolean(code),
        retry: false,
        ...options,
    });
}
export function useItemVariants(itemId, options) {
    const client = useInventoryClient();
    return useQuery({
        queryKey: inventoryKeys.items.variants(itemId ?? ''),
        queryFn: ({ signal }) => client.items.listVariants(itemId, signal),
        enabled: Boolean(itemId),
        ...options,
    });
}
export function useItemBarcodes(itemId, options) {
    const client = useInventoryClient();
    return useQuery({
        queryKey: inventoryKeys.items.barcodes(itemId ?? ''),
        queryFn: ({ signal }) => client.items.listBarcodes(itemId, signal),
        enabled: Boolean(itemId),
        ...options,
    });
}
export function useCreateItem() {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (input, operation) => client.items.create(input, operation),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: inventoryKeys.items.all() });
        },
    });
}
export function useUpdateItem(itemId) {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (input, operation) => client.items.update(itemId, input, operation),
        onSuccess: (item) => {
            queryClient.setQueryData(inventoryKeys.items.detail(itemId), item);
            void queryClient.invalidateQueries({ queryKey: inventoryKeys.items.all() });
        },
    });
}
export function useItemGroups(options) {
    const client = useInventoryClient();
    return useQuery({
        queryKey: inventoryKeys.itemGroups.list(),
        queryFn: ({ signal }) => client.itemGroups.list(signal),
        ...options,
    });
}
export function useCreateItemGroup() {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (input, operation) => client.itemGroups.create(input, operation),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: inventoryKeys.itemGroups.all() });
        },
    });
}
export function useUpdateItemGroup(groupId) {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (input, operation) => client.itemGroups.update(groupId, input, operation),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: inventoryKeys.itemGroups.all() });
        },
    });
}
/**
 * Resolve a scanned barcode.
 *
 * Note `uom_id` on the result: a carton barcode and a single barcode resolve to the same
 * item and mean different quantities. A scan path that ignores it books one unit for a
 * case — put the resolved UOM through `useConvertQuantity`, never a local multiplication.
 */
export function useResolveBarcode(barcode, options) {
    const client = useInventoryClient();
    return useQuery({
        queryKey: inventoryKeys.barcodes.resolve(barcode ?? ''),
        queryFn: ({ signal }) => client.barcodes.resolve(barcode, signal),
        enabled: Boolean(barcode),
        retry: false,
        ...options,
    });
}
export function useCreateBarcode() {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (input, operation) => client.barcodes.create(input, operation),
        onSuccess: (barcode) => {
            void queryClient.invalidateQueries({ queryKey: inventoryKeys.items.barcodes(barcode.item_id) });
            void queryClient.invalidateQueries({ queryKey: inventoryKeys.barcodes.all() });
        },
    });
}
export function useCreateItemVariant(itemId) {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (input, operation) => client.items.createVariant(itemId, input, operation),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: inventoryKeys.items.variants(itemId) });
        },
    });
}
//# sourceMappingURL=items.js.map